// vg-binding: P7.D-66
//
// apps/vendor/src/features/orders/detail/ArtworkPanel.tsx
//
// Artwork panel (fix/vendor-batch-e). When the detail payload carries
// `artwork_files[]` ({ area, name, url }) the panel renders one download
// link per file and enables "Download all" (opens each file in turn).
// TOLERANT to the field being absent (older BE builds): falls back to the
// original placeholder body with the button disabled, so nothing breaks.

import { Button } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import type { ArtworkFile } from '../api.js';

interface ArtworkPanelProps {
  /** Optional — hidden/placeholder when the BE does not send the field. */
  files?: ArtworkFile[];
}

export function ArtworkPanel({ files }: ArtworkPanelProps): JSX.Element {
  const { t } = useTranslation('orders');
  const rows = files ?? [];
  const hasFiles = rows.length > 0;

  const downloadAll = (): void => {
    // Sequential open per file — enough for the batch; a client-side zip can
    // replace this later without changing the BE contract.
    for (const f of rows) {
      window.open(f.url, '_blank', 'noopener');
    }
  };

  return (
    <section
      data-testid="order-detail-artwork-panel"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-pw-text">{t('artwork.title')}</h3>
        <Button
          type="button"
          variant="outline"
          size="xs"
          data-testid="artwork-download-all-btn"
          disabled={!hasFiles}
          title={hasFiles ? undefined : t('artwork.downloadAllTitle')}
          onClick={hasFiles ? downloadAll : undefined}
        >
          {t('artwork.downloadAll')}
        </Button>
      </div>
      {hasFiles ? (
        <ul className="flex flex-col gap-2" data-testid="artwork-file-list">
          {rows.map((f) => (
            <li
              key={`${f.area}-${f.url}`}
              className="flex items-center justify-between rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-sm"
            >
              <span className="mr-3 shrink-0 rounded-full border border-pw-border bg-pw-bg px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-pw-text-muted">
                {f.area}
              </span>
              <a
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                download={f.name}
                className="min-w-0 flex-1 truncate text-right font-medium text-pw-primary hover:underline"
              >
                {f.name}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center">
          <p className="text-sm text-pw-text-muted">
            {t('artwork.preview')}
          </p>
        </div>
      )}
    </section>
  );
}

export default ArtworkPanel;
